import {
  Server,
  ShieldCheck,
  HardDrive,
  Lock,
  WifiOff,
  Cloud,
  ArrowRight,
  ExternalLink,
  Check,
  Cpu,
} from 'lucide-react';
import { Button, FeatureIcon } from '@/components/ui';
import { Card } from '@/components/ui/Card';
import { SectionDivider } from '@/components/decorative';

const FNK = 'https://fnk.ai';
const FNK_CONTACT = 'mailto:sales@fnk.ai?subject=Private%20deployment%20enquiry%20via%20DialogMe';

const triggers = [
  {
    icon: Lock,
    title: 'A data-residency rule',
    detail: 'Conversations, documents and model calls have to stay inside your own network or jurisdiction.',
  },
  {
    icon: ShieldCheck,
    title: 'An audit or a client contract',
    detail: 'Health, legal, financial, public sector — or a customer who requires it in writing.',
  },
  {
    icon: Cpu,
    title: 'A scale problem',
    detail: 'Volume that belongs on dedicated accelerators rather than a shared managed platform.',
  },
];

const forms = [
  {
    icon: Server,
    name: 'Partial box',
    tagline: 'The same stack, on hardware you already own.',
    points: ['Installed into your existing data centre', 'Stack, Harness, connectors and console', 'Runs against a published hardware specification'],
  },
  {
    icon: HardDrive,
    name: 'Full box',
    tagline: 'Licensed hardware, preinstalled, ready to run.',
    points: ['Server hardware sourced and validated for the stack', 'Air-gapped where required', 'One vendor accountable for the whole system'],
  },
];

const deployments = [
  { icon: Cloud, title: 'Managed', detail: 'DialogMe runs it. Live in minutes.', who: 'DialogMe' },
  { icon: Lock, title: 'Private cloud', detail: 'Your tenancy, your controls.', who: 'Fnk' },
  { icon: Server, title: 'On-premises', detail: 'Your data centre, your network.', who: 'Fnk' },
  { icon: WifiOff, title: 'Air-gapped', detail: 'No outbound connectivity at all.', who: 'Fnk' },
];

export function EnterprisePage() {
  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-10 md:py-20">
        {/* Hero */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 rounded-full mb-6 border border-indigo-500/20">
            <ShieldCheck className="w-5 h-5 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">Enterprise</span>
          </div>
          <h1 className="text-hero-mobile md:text-hero-desktop font-extrabold tracking-heading mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
            Outgrow the Subscription. Keep the Product.
          </h1>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed mb-8">
            DialogMe is a managed platform. When a business needs the same service inside its own
            environment, it gets our own AI stack,{' '}
            <a href={FNK} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 underline decoration-indigo-500/40 underline-offset-2">
              Fnk
            </a>
            , as a private deployment.
          </p>
          <a href={FNK} target="_blank" rel="noopener noreferrer" className="inline-block w-full sm:w-auto">
            <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ExternalLink className="w-4 h-4" />}>
              Private deployment at fnk.ai
            </Button>
          </a>
        </div>

        <SectionDivider variant="gradient" />

        {/* When it applies */}
        <div className="my-16 md:my-20">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3 text-center">
            When a Private Deployment Is the Right Answer
          </h2>
          <p className="text-sm sm:text-base text-slate-400 text-center max-w-2xl mx-auto mb-10 leading-relaxed">
            Most businesses never need one. Three situations do.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {triggers.map((t) => (
              <Card key={t.title} variant="default" hoverable className="p-6">
                <FeatureIcon gradient="from-indigo-500/20 to-purple-500/20" className="mb-4">
                  <t.icon className="w-5 h-5 text-indigo-400" />
                </FeatureIcon>
                <h3 className="text-base font-semibold text-slate-100 tracking-tight mb-1.5">{t.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{t.detail}</p>
              </Card>
            ))}
          </div>
        </div>

        <SectionDivider variant="gradient" />

        {/* Same product, different owner of the hardware */}
        <div className="my-16 md:my-20">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3 text-center">
            The Same Stack, Two Ways to Own It
          </h2>
          <p className="text-sm sm:text-base text-slate-400 text-center max-w-2xl mx-auto mb-10 leading-relaxed">
            Fnk is the stack DialogMe runs on — framework, control layer, inference engine and the
            Harness — delivered as a box. Your service types, integrations and Harness packs carry over.
            What changes is who owns the hardware, never what the product can do.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {forms.map((form) => (
              <Card key={form.name} variant="highlighted" className="p-6 sm:p-8">
                <FeatureIcon gradient="from-indigo-500/20 to-purple-500/20" className="mb-4">
                  <form.icon className="w-6 h-6 text-indigo-400" />
                </FeatureIcon>
                <h3 className="text-xl font-bold text-white tracking-tight mb-2">{form.name}</h3>
                <p className="text-sm text-indigo-300 mb-5">{form.tagline}</p>
                <div className="space-y-2.5">
                  {form.points.map((p) => (
                    <div key={p} className="flex items-start gap-2 text-[13px] text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
          <p className="text-xs text-slate-500 text-center mt-6 max-w-2xl mx-auto leading-relaxed">
            Pricing, delivery timeline and licensing are published at fnk.ai. A DialogMe account
            that moves keeps its configuration — there is nothing to migrate and no second product
            to learn.
          </p>
        </div>

        <SectionDivider variant="gradient" />

        {/* Where it runs */}
        <div className="my-16 md:my-20">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-10 text-center">
            Where It Runs, and Who Runs It
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {deployments.map((d) => (
              <Card key={d.title} variant={d.who === 'DialogMe' ? 'highlighted' : 'elevated'} className="p-5 text-center">
                <FeatureIcon gradient="from-cyan-500/20 to-blue-500/20" className="mx-auto mb-3">
                  <d.icon className="w-5 h-5 text-cyan-400" />
                </FeatureIcon>
                <h3 className="text-sm font-semibold text-slate-100 tracking-tight mb-1.5">{d.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed mb-2">{d.detail}</p>
                <p className="text-[11px] font-medium text-indigo-300/80">{d.who}</p>
              </Card>
            ))}
          </div>
        </div>

        <SectionDivider variant="gradient" />

        {/* Closing CTA */}
        <div className="my-16 md:my-20">
          <Card variant="cta" className="text-center p-6 sm:p-8 md:p-10">
            <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3">
              Talk to Fnk About Your Deployment
            </h2>
            <p className="text-slate-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              Tell us what you want to run and where it has to run. Your DialogMe service types
              carry over. Not sure you need it? Start on the managed platform — it is
              the same product.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={FNK_CONTACT} className="w-full sm:w-auto">
                <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="w-4 h-4" />}>
                  sales@fnk.ai
                </Button>
              </a>
              <a href="https://console.dialogme.ai" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" className="w-full sm:w-auto">
                  Start on the Managed Platform
                </Button>
              </a>
            </div>
          </Card>
        </div>
      </div>
    </main>
  );
}
